import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import { Check, Sparkles, ArrowRight, ArrowLeft, Clock, Loader2 } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Price calculations
const PRICES = {
  invoice: { monthly: 4.99 },
  pos: { monthly: 19.99 },
};
// Yearly = 12 months x monthly price - 15% discount
PRICES.invoice.yearly = Math.round(PRICES.invoice.monthly * 12 * 0.85 * 100) / 100; // 50.90
PRICES.invoice.yearlyMonthly = Math.round(PRICES.invoice.yearly / 12 * 100) / 100;  // 4.24
PRICES.pos.yearly = Math.round(PRICES.pos.monthly * 12 * 0.85 * 100) / 100;         // 203.90
PRICES.pos.yearlyMonthly = Math.round(PRICES.pos.yearly / 12 * 100) / 100;          // 16.99

// 50% off yearly for special offer
PRICES.invoice.yearlyOffer = Math.round(PRICES.invoice.yearly * 0.50 * 100) / 100;  // 25.45
PRICES.pos.yearlyOffer = Math.round(PRICES.pos.yearly * 0.50 * 100) / 100;          // 101.95

const PricingPage = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const highlightPlan = searchParams.get('plan');
  const [billing, setBilling] = useState('yearly'); // 'monthly' | 'yearly'
  const [loadingPlan, setLoadingPlan] = useState(null);

  const invoiceFeatures = t('pricing_invoice_features') || [];
  const posFeatures = t('pricing_pos_features') || [];

  const getPrice = (plan) => {
    const p = PRICES[plan];
    if (billing === 'monthly') return { display: p.monthly.toFixed(2), period: t('pricing_per_month') };
    return { display: p.yearlyMonthly.toFixed(2), period: t('pricing_per_month'), yearTotal: p.yearly.toFixed(2) };
  };

  const handleSubscribe = async (plan) => {
    const packageId = billing === 'yearly' ? `${plan}_yearly_offer` : `${plan}_monthly`;
    setLoadingPlan(plan);
    try {
      const res = await fetch(`${API}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }),
      });
      if (!res.ok) throw new Error('Checkout failed');
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error('Checkout error:', e);
      alert(t('pay_error') || 'Error starting checkout. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const invoicePrice = getPrice('invoice');
  const posPrice = getPrice('pos');

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="pricing-page">
      <Navbar />

      <main className="vf-section">
        <div className="vf-container">
          {/* Back Link */}
          <Link
            to={`/${language}`}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1e3a5f] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('pos_back')}
          </Link>

          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
            <h1 className="vf-heading-1 text-[#1e3a5f] mb-4">{t('pricing_title')}</h1>
            <p className="vf-body-large text-slate-600 max-w-2xl mx-auto">{t('pricing_subtitle')}</p>

            {/* Special Offer Banner */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-3 mt-6 px-6 py-3 bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8f] text-white font-bold rounded-sm shadow-lg"
              data-testid="pricing-special-offer"
            >
              <Clock className="w-5 h-5 text-yellow-300 flex-shrink-0" />
              <span className="text-sm sm:text-base">{t('pricing_launch')}</span>
              <Sparkles className="w-5 h-5 text-yellow-300 flex-shrink-0" />
            </motion.div>
          </motion.div>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-12" data-testid="billing-toggle">
            <span className={`text-sm font-medium ${billing === 'monthly' ? 'text-[#1e3a5f]' : 'text-slate-400'}`}>
              {t('pricing_monthly')}
            </span>
            <button
              onClick={() => setBilling(b => b === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative w-16 h-8 rounded-full transition-colors ${billing === 'yearly' ? 'bg-[#22c55e]' : 'bg-slate-300'}`}
              data-testid="billing-toggle-btn"
            >
              <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${billing === 'yearly' ? 'translate-x-9' : 'translate-x-1'}`} />
            </button>
            <span className={`text-sm font-medium ${billing === 'yearly' ? 'text-[#1e3a5f]' : 'text-slate-400'}`}>
              {t('pricing_yearly')}
            </span>
            {billing === 'yearly' && (
              <motion.span initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                className="bg-[#22c55e] text-white text-xs font-bold px-3 py-1 rounded-full"
              >
                {t('pricing_yearly_save')}
              </motion.span>
            )}
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Invoice Plan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className={`vf-card-pricing ${highlightPlan === 'invoice' ? 'border-[#22c55e] ring-4 ring-[#22c55e]/20' : ''}`}
              data-testid="pricing-page-invoice-card"
            >
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-[#1e3a5f]">{t('pricing_invoice_title')}</h3>
                  <p className="text-slate-500">{t('pricing_invoice_subtitle')}</p>
                </div>

                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-bold text-[#1e3a5f]" data-testid="invoice-price">
                      €{invoicePrice.display}
                    </span>
                    <span className="text-xl text-slate-500">{invoicePrice.period}</span>
                  </div>
                  {billing === 'yearly' && (
                    <p className="text-sm text-slate-400 mt-1">
                      €{invoicePrice.yearTotal}{t('pricing_per_year')} · {t('pricing_billed_yearly')}
                    </p>
                  )}
                </div>

                {/* Special offer for yearly */}
                {billing === 'yearly' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                    className="bg-amber-50 border-2 border-amber-200 px-4 py-3"
                    data-testid="invoice-offer-badge"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-amber-600" />
                      <span className="text-amber-800 font-bold text-sm">50% OFF</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="line-through text-slate-400 text-sm">€{PRICES.invoice.yearly.toFixed(2)}</span>
                      <span className="text-[#22c55e] font-bold text-lg">€{PRICES.invoice.yearlyOffer.toFixed(2)}{t('pricing_per_year')}</span>
                    </div>
                    <p className="text-xs text-amber-700 mt-1">€{(PRICES.invoice.yearlyOffer / 12).toFixed(2)}{t('pricing_per_month')}</p>
                  </motion.div>
                )}

                <ul className="space-y-3">
                  {Array.isArray(invoiceFeatures) && invoiceFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe('invoice')}
                  disabled={loadingPlan === 'invoice'}
                  className="vf-btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="pricing-page-invoice-button"
                >
                  {loadingPlan === 'invoice' ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  {t('pricing_cta_invoice')}
                  {loadingPlan !== 'invoice' && <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
            </motion.div>

            {/* POS Plan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className={`vf-card-pricing border-[#22c55e] scale-[1.02] relative overflow-hidden ${highlightPlan === 'pos' ? 'ring-4 ring-[#22c55e]/20' : ''}`}
              data-testid="pricing-page-pos-card"
            >
              <div className="absolute top-0 right-0 bg-[#22c55e] text-white px-6 py-2 text-sm font-bold">
                {t('pricing_popular')}
              </div>

              <div className="space-y-6 pt-4">
                <div>
                  <h3 className="text-2xl font-bold text-[#1e3a5f]">{t('pricing_pos_title')}</h3>
                  <p className="text-slate-500">{t('pricing_pos_subtitle')}</p>
                </div>

                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-bold text-[#22c55e]" data-testid="pos-price">
                      €{posPrice.display}
                    </span>
                    <span className="text-xl text-slate-500">{posPrice.period}</span>
                  </div>
                  {billing === 'yearly' && (
                    <p className="text-sm text-slate-400 mt-1">
                      €{posPrice.yearTotal}{t('pricing_per_year')} · {t('pricing_billed_yearly')}
                    </p>
                  )}
                </div>

                {/* Special offer for yearly */}
                {billing === 'yearly' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                    className="bg-amber-50 border-2 border-amber-200 px-4 py-3"
                    data-testid="pos-offer-badge"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-amber-600" />
                      <span className="text-amber-800 font-bold text-sm">50% OFF</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="line-through text-slate-400 text-sm">€{PRICES.pos.yearly.toFixed(2)}</span>
                      <span className="text-[#22c55e] font-bold text-lg">€{PRICES.pos.yearlyOffer.toFixed(2)}{t('pricing_per_year')}</span>
                    </div>
                    <p className="text-xs text-amber-700 mt-1">€{(PRICES.pos.yearlyOffer / 12).toFixed(2)}{t('pricing_per_month')}</p>
                  </motion.div>
                )}

                <ul className="space-y-3">
                  {Array.isArray(posFeatures) && posFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe('pos')}
                  disabled={loadingPlan === 'pos'}
                  className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="pricing-page-pos-button"
                >
                  {loadingPlan === 'pos' ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  {t('pricing_cta_pos')}
                  {loadingPlan !== 'pos' && <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;
